import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { CasePrepUscisFormsAPI } from "./api";
import type {
  CasePrepUscisFilledForm,
  CasePrepUscisFormCatalogEntry
} from "./types";

type ContextValue = {
  forms: CasePrepUscisFilledForm[];
  catalog: CasePrepUscisFormCatalogEntry[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  deleteForm: (filledFormId: string) => Promise<void>;
  upsertForm: (form: CasePrepUscisFilledForm) => void;
};

const CasePrepUscisFormsContext = createContext<ContextValue | undefined>(
  undefined
);

export const CasePrepUscisFormsProvider = ({
  individualId,
  caseId,
  children
}: {
  individualId: string;
  caseId: string;
  children: React.ReactNode;
}) => {
  const [forms, setForms] = useState<CasePrepUscisFilledForm[]>([]);
  const [catalog, setCatalog] = useState<CasePrepUscisFormCatalogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!individualId || !caseId) return;
    setIsLoading(true);
    setError(null);
    try {
      const [nextForms, nextCatalog] = await Promise.all([
        CasePrepUscisFormsAPI.listFilledForms(individualId, caseId),
        CasePrepUscisFormsAPI.listCatalog()
      ]);
      setForms(nextForms);
      setCatalog(nextCatalog);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Unable to load forms"));
    } finally {
      setIsLoading(false);
    }
  }, [caseId, individualId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const deleteForm = useCallback(
    async (filledFormId: string) => {
      await CasePrepUscisFormsAPI.deleteFilledForm(individualId, filledFormId);
      setForms((current) =>
        current.filter(
          (form) => form.case_extend_uscis_filled_form_id !== filledFormId
        )
      );
    },
    [individualId]
  );

  const upsertForm = useCallback((form: CasePrepUscisFilledForm) => {
    setForms((current) => {
      const exists = current.some(
        (item) =>
          item.case_extend_uscis_filled_form_id ===
          form.case_extend_uscis_filled_form_id
      );
      if (exists) {
        return current.map((item) =>
          item.case_extend_uscis_filled_form_id ===
          form.case_extend_uscis_filled_form_id
            ? form
            : item
        );
      }
      return [form, ...current];
    });
  }, []);

  const value = useMemo(
    () => ({
      forms,
      catalog,
      isLoading,
      error,
      refetch,
      deleteForm,
      upsertForm
    }),
    [catalog, deleteForm, error, forms, isLoading, refetch, upsertForm]
  );

  return (
    <CasePrepUscisFormsContext.Provider value={value}>
      {children}
    </CasePrepUscisFormsContext.Provider>
  );
};

export const useCasePrepUscisForms = () => {
  const context = useContext(CasePrepUscisFormsContext);
  if (!context) {
    throw new Error(
      "useCasePrepUscisForms must be used within CasePrepUscisFormsProvider"
    );
  }
  return context;
};
